// Combat HUD action bar layout: tiles are identified by their `data-action-tile` key and sit in a fixed cell of
// one of a few pages. A page is a list of cells in row-major order; an empty cell is null, so gaps stay put.

export const ACTION_BAR_PAGE_COUNT = 3;

/** Three pages of cells holding unique string keys or null; anything else in a stored layout is dropped. */
export function normalizePages(pages) {
  const seen = new Set();
  return Array.from({ length: ACTION_BAR_PAGE_COUNT }, (_, index) => {
    const cells = (Array.isArray(pages?.[index]) ? pages[index] : [])
      .map((key) => (typeof key === 'string' && key.length > 0 && !seen.has(key) && seen.add(key) ? key : null));
    while (cells.length && cells[cells.length - 1] === null) cells.pop();
    return cells;
  });
}

const usable = (cell, capacity, blocked) => cell < capacity && !blocked.has(cell);

/** First free cell on a page, or -1. */
export function firstFreeCell(page, capacity, blocked = new Set()) {
  for (let cell = 0; cell < capacity; cell += 1) if (usable(cell, capacity, blocked) && !page[cell]) return cell;
  return -1;
}

/**
 * Where each present tile shows. Stored tiles keep their cell; a tile whose cell is gone (the bar got smaller,
 * a button now sits there) and new tiles take the first free cell, page by page. Stored tiles that are not on
 * the bar right now (a conditional tile) leave their cell free.
 */
export function placeTiles(pages, presentKeys, capacity, blocked = new Set()) {
  const present = new Set(presentKeys);
  const placed = new Set();
  const result = normalizePages(pages).map(() => []);
  normalizePages(pages).forEach((page, pageIndex) => page.forEach((key, cell) => {
    if (key && present.has(key) && !placed.has(key) && usable(cell, capacity, blocked)) { result[pageIndex][cell] = key; placed.add(key); }
  }));
  const waiting = [...normalizePages(pages).flat().filter((key) => key && present.has(key) && !placed.has(key)), ...presentKeys.filter((key) => !placed.has(key))];
  for (const key of waiting) {
    if (placed.has(key)) continue;
    placed.add(key);
    const pageIndex = result.findIndex((page) => firstFreeCell(page, capacity, blocked) >= 0);
    if (pageIndex >= 0) result[pageIndex][firstFreeCell(result[pageIndex], capacity, blocked)] = key;
    else result[result.length - 1].push(key); // every cell on every page is taken: keep it rather than drop it
  }
  return result.map((page) => Array.from({ length: page.length }, (_, cell) => page[cell] || null));
}

/** Puts a tile in a cell; a tile already there swaps into the moved tile's old cell. */
export function moveTile(placement, key, toPage, toCell) {
  const result = placement.map((page) => [...page]);
  const fromPage = result.findIndex((page) => page.includes(key));
  const fromCell = fromPage >= 0 ? result[fromPage].indexOf(key) : -1;
  const occupant = result[toPage]?.[toCell] || null;
  if (fromPage >= 0) result[fromPage][fromCell] = null;
  if (occupant && occupant !== key && fromPage >= 0) result[fromPage][fromCell] = occupant;
  while (result[toPage].length < toCell) result[toPage].push(null);
  result[toPage][toCell] = key;
  return result.map((page) => Array.from({ length: page.length }, (_, cell) => page[cell] || null));
}

/**
 * The layout to save: the placed tiles, plus stored tiles that are not on the bar right now in their old cell
 * when it is still free, so a conditional tile returns to its spot when it reappears.
 */
export function layoutToSave(storedPages, placement) {
  const placed = new Set(placement.flat().filter(Boolean));
  return normalizePages(storedPages).map((stored, pageIndex) => {
    const cells = [...placement[pageIndex]];
    stored.forEach((key, cell) => { if (key && !placed.has(key) && !cells[cell]) { while (cells.length < cell) cells.push(null); cells[cell] = key; } });
    return normalizePages([cells])[0];
  });
}

/** Grid cell (row-major) under a point, given the grid's content box and track sizes. */
export function slotAt({ x, y, left, top, columns, tileWidth, tileHeight, gap }) {
  const column = Math.max(0, Math.min(columns - 1, Math.floor((x - left + gap / 2) / (tileWidth + gap))));
  const row = Math.max(0, Math.floor((y - top + gap / 2) / (tileHeight + gap)));
  return row * columns + column;
}

const quote = (key) => `"${String(key).replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\a ')}"`;

/** CSS that puts each tile of the current page in its cell and hides the tiles on other pages. */
export function layoutCss(scope, placement, currentPage, columns, draggingKey = null) {
  const rules = [];
  placement.forEach((page, pageIndex) => page.forEach((key, cell) => {
    if (!key) return;
    const selector = `${scope} [data-action-tile=${quote(key)}]`;
    // grid-row/column place the tile; order keeps the sequence where the bar is a flex row (phones).
    rules.push(pageIndex === currentPage
      ? `${selector}{grid-row:${Math.floor(cell / columns) + 1};grid-column:${(cell % columns) + 1};order:${cell};}`
      : `${selector}{display:none !important;}`);
  }));
  if (draggingKey) rules.push(`${scope} [data-action-tile=${quote(draggingKey)}]{opacity:.25;}`);
  return rules.join('\n');
}
